// ══════════════════════════════════════════════════════════════════════════════════════════════
// L'ASSAINISSEUR — ce qui entre dans un corps d'e-mail, et rien d'autre.
//
// POURQUOI IL EXISTE. Un éditeur `contentEditable` accepte le COLLAGE : n'importe quel HTML peut y
// entrer, y compris un `<script>`, un `<iframe>`, un `onerror=` ou un `href="javascript:"`. Le corps
// est ensuite stocké, puis renvoyé à l'aperçu (`dangerouslySetInnerHTML`) et expédié à des milliers
// de boîtes. Le dépôt a **déjà payé** cette classe deux fois : le `javascript:` du bouton « retourner
// à l'application » de l'IdP, et la table d'échappement anti-injection partie au catalogue de
// traduction.
//
// ⚠️ 🔴 L'ÉCHAPPEMENT TRAITE LA FORME D'UNE VALEUR, JAMAIS SON PROTOCOLE. `esc()` n'a jamais protégé
// d'un `javascript:` — c'est écrit au catalogue des formes de défaut, et c'est la raison pour laquelle
// les URL sont jugées à part, sur leur SCHÉMA.
//
// ⚠️ 🔴 LISTE D'ADMISSION, JAMAIS LISTE D'INTERDICTION. Une liste de choses interdites est fausse dès
// qu'un navigateur en invente une (`<template>`, `<math>`, un attribut `on…` de plus). Ici le
// PRODUCTEUR est contraint — notre éditeur n'émet qu'une poignée de balises —, donc la liste de ce
// qu'on accepte est courte, lisible, et vraie par construction.
//
// ⚠️ IL TOURNE AU SERVEUR, À L'ÉCRITURE. Assainir dans le navigateur ne protège de rien : l'API est la
// vraie porte. Un second passage au rendu est gratuit et couvre les corps écrits avant la règle.
//
// ⚠️ 🔴 ET IL N'UTILISE AUCUN DOM. Ce paquet est pur (il tourne sous Node, sous Hermes et dans un
// navigateur) : il ne peut pas s'appuyer sur `DOMParser`, qui n'existe pas partout — et surtout, un
// assainisseur qui PARSE puis RÉ-ÉMET dépend de la fidélité du ré-émetteur. Ici on réécrit le texte
// balise par balise, et **tout ce qui n'est pas reconnu disparaît**.
// ══════════════════════════════════════════════════════════════════════════════════════════════

/**
 * LES BALISES ADMISES — exactement ce que l'éditeur produit, plus la mise en forme d'un collage
 * raisonnable.
 * ⚠️ `font` EST LÀ PARCE QUE `document.execCommand('fontSize')` L'ÉMET : l'exclure ferait disparaître
 * la taille dès l'enregistrement, c'est-à-dire un bouton qui fait semblant.
 */
const BALISES = new Set([
  'b', 'strong', 'i', 'em', 'u', 's', 'br', 'p', 'div', 'span', 'font',
  'ul', 'ol', 'li', 'h1', 'h2', 'h3', 'a',
]);

/** Les balises SANS fermeture — les seules dont un `<x>` isolé est légitime. */
const ORPHELINES = new Set(['br']);

/**
 * LES ATTRIBUTS ADMIS, PAR BALISE.
 * ⚠️ 🔴 AUCUN `style` GÉNÉRAL. Un `style` libre ouvre `background:url(javascript:…)`, les polices
 * distantes et le débordement hors du cadre ; et il n'apporte rien ici, l'éditeur produisant des
 * balises sémantiques. `font` garde `size`/`color` parce que `execCommand` n'a que ça.
 */
const ATTRIBUTS: Record<string, Set<string>> = {
  a: new Set(['href', 'title']),
  font: new Set(['size', 'color']),
};

/** Les schémas d'URL admis sur un lien. ⚠️ `javascript:`, `data:` et `vbscript:` en sont EXCLUS. */
const SCHEMAS = ['http:', 'https:', 'mailto:', 'tel:'];

/** Une couleur : trois ou six hexadécimaux, ou un mot simple. Tout le reste tombe. */
const COULEUR = /^(#[0-9a-f]{3}|#[0-9a-f]{6}|[a-z]{3,20})$/i;

/** Une taille `execCommand` : 1 à 7, et rien d'autre. */
const TAILLE = /^[1-7]$/;

/**
 * Une URL est-elle sûre ?
 *
 * ⚠️ 🔴 ON JUGE LE SCHÉMA, PAS LA FORME — et avec une LISTE D'ADMISSION, c'est tout ce qu'il faut.
 * Un `href` obfusqué (`java&#9;script:`, `&#106;avascript:`, ` javascript:`) ne commence par AUCUN
 * schéma admis : il tombe, sans qu'on ait rien à décoder.
 *
 * ⚠️ 🔴 ET C'EST POURQUOI ON NE NORMALISE PRESQUE PAS. La première version décodait les entités et
 * retirait TOUS les blancs, par réflexe de liste d'interdiction — trois mutations ont SURVÉCU et
 * l'ont dit : contre une liste d'ADMISSION, normaliser ne peut que rendre la règle **plus
 * permissive**. `&#104;ttps://x.fr` décodé devient acceptable alors qu'il ne l'était pas, et il
 * repartirait de toute façon ÉCHAPPÉ dans le `href`, donc cassé. Une normalisation qui ne peut
 * qu'ouvrir la porte n'a pas sa place dans une fonction de sécurité.
 *
 * ⚠️ CE QUI RESTE EST BORNÉ ET JUSTIFIÉ : `trim` (les espaces autour d'une URL collée sont un
 * artefact de presse-papier, et ` javascript:` tombe quand même) et la casse (`HTTPS://` est une
 * adresse parfaitement valide). Aucun caractère INTERNE n'est retiré — `htt ps://` doit rester faux.
 *
 * ⚠️ UNE URL RELATIVE EST REFUSÉE : dans un e-mail, elle ne désigne rien chez le destinataire.
 */
export function urlSure(brut: string): boolean {
  const nu = brut.trim().toLowerCase();
  return SCHEMAS.some((s) => nu.startsWith(s));
}

/**
 * ÉCHAPPE UNE VALEUR — tout, y compris les `&` déjà suivis d'une entité.
 *
 * ⚠️ 🔴 C'EST LA FONCTION DES VALEURS, PAS DU TEXTE D'UN CORPS. Une valeur vient du dehors (un nom
 * saisi en caisse) : si elle contient littéralement « &amp; », c'est une donnée abîmée, et la rendre
 * telle quelle serait lui prêter un sens qu'elle n'a pas. On échappe donc TOUT.
 * ⚠️ L'ORDRE COMPTE : `&` en premier, sinon on ré-échapperait ses propres sorties.
 */
export function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * ÉCHAPPE LE TEXTE D'UN CORPS — en LAISSANT les entités déjà écrites.
 *
 * ⚠️ 🔴 SANS ELLE L'ASSAINISSEUR N'EST PAS IDEMPOTENT, et le rendu le rejoue PAR CONCEPTION (le
 * second passage est ce qui couvre les corps écrits avant la règle). Un corps stocké `3 &lt; 5`
 * ressortirait `3 &amp;lt; 5` à chaque envoi : le destinataire lirait « 3 &lt; 5 » en toutes
 * lettres. Le banc l'a pris à l'écriture — c'est le seul cas qui compare deux passages.
 *
 * ⚠️ LE PRIX EST CONNU ET ASSUMÉ : quelqu'un qui tape littéralement « &amp; » dans l'éditeur verra
 * « & » chez le destinataire. C'est le compromis de tous les assainisseurs qui relisent du HTML, et
 * il vaut mieux que de casser l'idempotence — un texte légèrement réinterprété se voit à l'aperçu,
 * un double échappement ne se voit qu'à la réception.
 */
function escapeTexte(s: string): string {
  return s
    // ⚠️ Un `&` qui n’ouvre PAS une entité valide est un vrai esperluette, et il s’échappe.
    .replace(/&(?!(?:#d+|#x[0-9a-fA-F]+|[a-zA-Z][a-zA-Z0-9]{1,30});)/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

/** Les attributs d'une balise, relus un par un. */
function attributsSurs(balise: string, brut: string): string {
  const admis = ATTRIBUTS[balise];
  if (!admis) return '';
  const sortie: string[] = [];
  for (const m of brut.matchAll(/([a-zA-Z-]+)\s*=\s*("([^"]*)"|'([^']*)'|([^\s"'>]+))/g)) {
    const nom = m[1]!.toLowerCase();
    if (!admis.has(nom)) continue;
    const valeur = m[3] ?? m[4] ?? m[5] ?? '';
    if (nom === 'href' && !urlSure(valeur)) continue;
    if (nom === 'color' && !COULEUR.test(valeur)) continue;
    if (nom === 'size' && !TAILLE.test(valeur)) continue;
    sortie.push(`${nom}="${escapeHtml(valeur)}"`);
  }
  // ⚠️ 🔴 UN LIEN SANS `href` SÛR PERD SON LIEN, IL NE DISPARAÎT PAS : le texte cliquable reste
  //    lisible. Faire disparaître le `<a>` entier retirerait des mots du message sans le dire.
  if (balise === 'a') sortie.push('rel="noopener noreferrer"', 'target="_blank"');
  return sortie.length > 0 ? ` ${sortie.join(' ')}` : '';
}

/**
 * ASSAINIT UN CORPS HTML.
 *
 * ⚠️ 🔴 LE CONTENU DES BALISES DANGEREUSES EST JETÉ AVEC ELLES. Retirer seulement `<script>` et
 * `</script>` laisserait le code entre les deux, en clair, dans le corps du message — et le même
 * raisonnement vaut pour `<style>` (qui peut porter des expressions) et pour les commentaires
 * conditionnels. On supprime donc l'ÉLÉMENT, pas la balise.
 *
 * ⚠️ 🔴 ET LE TEXTE HORS BALISE EST RÉ-ÉCHAPPÉ. Sans cela, un `<` isolé collé par un humain
 * (« 3 < 5 ») ressortirait tel quel et casserait la lecture du corps chez le destinataire.
 */
export function sanitizeHtml(brut: string): string {
  // ① Les éléments dont le CONTENU doit disparaître avec eux.
  let s = brut
    .replace(/<!--[\s\S]*?-->/g, '')
    .replace(/<\s*(script|style|iframe|object|embed|svg|math|template|noscript)\b[\s\S]*?<\s*\/\s*\1\s*>/gi, '')
    // ⚠️ UNE OUVERTURE NON FERMÉE COMPTE AUSSI : `<script>` sans `</script>` avale la suite du
    //    document chez le destinataire. On coupe à partir d'elle.
    .replace(/<\s*(script|style|iframe|object|embed|svg|math|template|noscript)\b[\s\S]*$/gi, '');

  // ② Chaque balise restante est relue ; le texte entre elles est ré-échappé.
  let sortie = '';
  let curseur = 0;
  for (const m of s.matchAll(/<\s*(\/?)\s*([a-zA-Z][a-zA-Z0-9]*)((?:[^>"']|"[^"]*"|'[^']*')*)>/g)) {
    sortie += escapeTexte(s.slice(curseur, m.index));
    curseur = m.index + m[0].length;
    const fermante = m[1] === '/';
    const balise = m[2]!.toLowerCase();
    if (!BALISES.has(balise)) continue;            // ⚠️ inconnue : elle disparaît, son texte reste
    if (fermante) {
      if (!ORPHELINES.has(balise)) sortie += `</${balise}>`;
      continue;
    }
    sortie += ORPHELINES.has(balise) ? '<br />' : `<${balise}${attributsSurs(balise, m[3] ?? '')}>`;
  }
  sortie += escapeTexte(s.slice(curseur));
  s = sortie;

  // ③ Les balises ouvertes et jamais fermées sont closes dans l'ordre inverse — un `<b>` orphelin
  //    mettrait en gras tout ce qui suit dans le client de messagerie, y compris le pied de page.
  return fermerLesOuvertes(s);
}

/** Ferme ce qui est resté ouvert, du plus récent au plus ancien. */
function fermerLesOuvertes(s: string): string {
  const pile: string[] = [];
  for (const m of s.matchAll(/<(\/?)([a-z][a-z0-9]*)[^>]*>/g)) {
    const balise = m[2]!;
    if (balise === 'br') continue;
    if (m[1] === '/') {
      const i = pile.lastIndexOf(balise);
      if (i >= 0) pile.splice(i, 1);
    } else {
      pile.push(balise);
    }
  }
  return s + pile.reverse().map((b) => `</${b}>`).join('');
}
